import moment from 'moment';
import Api from 'services/Api';
import _ from 'lodash';
import roundValue from 'mixins/roundValue';

/**
 * Little finger helper.
 *
 * @param {string} value
 * @return {Moment}
 */
const m = value => moment(value, 'DD/MM/YYYY');

/**
 * Convert to server date.
 * @param {string} date
 * @return {string}
 */
const serverDate = date => m(date).format('YYYY-MM-DD');

/**
 * Is the value null?
 *
 * @param value
 */
const notNull = value => value !== null;

const camelCaseObjectKeys = src => {
    let ret = {};
    let keys = Object.keys(src);
    keys.forEach((key, i) => ret[_.camelCase(key)] = src[key]);
    return ret;
};

const camelCaseObjectKeysDeep = src => _.chain(camelCaseObjectKeys(src))
    .mapValues(value => _.isObjectLike(value) ? camelCaseObjectKeysDeep(value) : value)
    .value();

const checkForServerDates = src => _.chain(src)
    .mapValues(value => {
        if (_.isObjectLike(value)) {
            return checkForServerDates(value);
        }
        // Crude regex for dates
        if (_.isString(value) && /[\d]{4}-[\d]{1,2}-[\d]{1,2}/.test(value)) {
            return moment(value, 'YYYY-MM-DD').format('DD/MM/YYYY');
        }
        return value;
    })
    .value();

// Mutations Types
const mutationTypes = {
    UPDATE_DECEASED: 'UPDATE_DECEASED',
    UPDATE_CLAIM: 'UPDATE_CLAIM',
    UPDATE_CLAIM_ID: 'UPDATE_CLAIM_ID',
    UPDATE_NAME_AND_REFERENCE: 'UPDATE_NAME_AND_REFERENCE',
    UPDATE_DEPENDENCY_TYPE: 'UPDATE_DEPENDENCY_TYPE',
    SET_AGE: 'SET_AGE',
    UPDATE_RESULT: 'UPDATE_RESULT',
    RESET_RESULT: 'RESET_RESULT',
    UPDATE_CONTRIBUTION: 'UPDATE_CONTRIBUTION',
    RESET: 'RESET',
    UPDATE_FROM_SERVER_STEP: 'UPDATE_FROM_SERVER_STEP',
    ADD_OWN_HEAD_OF_LOSS: 'ADD_OWN_HEAD_OF_LOSS',
    UPDATE_OWN_HEAD_OF_LOSS: 'UPDATE_OWN_HEAD_OF_LOSS',
    DELETE_OWN_HEAD_OF_LOSS: 'DELETE_OWN_HEAD_OF_LOSS',
    START_CALCULATING: 'START_CALCULATING',
    STOP_CALCULATING: 'STOP_CALCULATING',
    UPDATE_COSTS: 'UPDATE_COSTS',
    UPDATE_CLAIMANT_SOLICITOR_ID: 'UPDATE_CLAIMANT_SOLICITOR_ID',
    UPDATE_DEFENDANT_ID: 'UPDATE_DEFENDANT_ID',
    SET_FORM_DIRTY: 'SET_FORM_DIRTY',
    SET_FORM_CLEAN: 'SET_FORM_CLEAN',
    UPDATE_GENERAL_DAMAGES: 'UPDATE_GENERAL_DAMAGES'

};

/**
 * Initial state
 * @return {{dependencyType: null, hasResults: boolean, claim: {type: string, diseaseType: null, assumedSettlement: null, proceduralStatus: null, numberOfDependants: null}, deceased: {gender: null, dateOfBirth: null, dateOfDeath: null, ageAtDeath: null, employmentType: null, onsetOfSymptoms: null, salary: null, occupation: {code: string, description: string}}, results: {calculation: {deceasedAgeDeath: null, periodBetweenDodAndAssessment: null, deceasedAgeAtAssessment: null, periodOfDependencyFromAssessment: null, ogdenDiscountRate: null, table28Multiplier: null, preAssessmentDiscount: null, postAssessmentDiscount: null}, damages: {generalDamages: null, pastLossOfEarnings: null, pastCostOfDiy: null, bereavementAward: null, care: null, funeralExpenses: null, miscellaneous: null, dependencyOnIncome: null, dependencyOnServices: null, totalDamages: null}, reserve: {claimantCosts: null, defenceCosts: null}, chart: {}}, contributions: {damages: number, claimant: number, defence: number}}}
 */
const initialState = () => {
    return {
        claimId: null,
        stepId: null,
        dependencyType: null,
        hasResults: false,
        dirtyForm: false,
        nameAndReference: {
            claimantName: null,
            claimantReference: null,
            yourReference: null,
            defendantName: {
                id: null,
                name: null,
                type: null
            },
            claimantSolicitors: {
                id: null,
                name: null,
                type: null
            },
        },
        claim: {
            type: 'fatal_accident',
            diseaseOrAccident: null,
            accidentType: null,
            diseaseType: null,
            assumedSettlement: null,
            proceduralStatus: null,
            numberOfDependants: null,
        },
        person: {
            gender: null,
            dateOfBirth: null,
            dateOfDeath: null,
            ageAtDeath: null,
            employmentType: null,
            onsetOfSymptoms: null,
            salary: null,
            occupation: {
                code: '',
                description: ''
            }
        },
        results: {
            calculation: {
                deceasedAgeDeath: null,
                periodBetweenDodAndAssessment: null,
                deceasedAgeAtAssessment: null,
                periodOfDependencyFromAssessment: null,
                ogdenDiscountRate: null,
                table28Multiplier: null,
                preAssessmentDiscount: null,
                postAssessmentDiscount: null,
                retirementDate: null
            },
            damages: {
                generalDamages: null,
                pastLossOfEarnings: null,
                pastCostOfDiy: null,
                bereavementAward: null,
                care: null,
                funeralExpenses: null,
                miscellaneous: null,
                dependencyOnIncome: null,
                dependencyOnServices: null,
                totalDamages: null,
            },
            reserve: {
                claimantCosts: null,
                defenceCosts: null
            },
            chart: {}
        },
        contributions: {
            damages: 100,
            claimant: 100,
            defence: 100,
        },
        ownHeadsOfLoss: [],
        calculating: false
    };
};

/**
 * Make a claim ready for the server.
 * @param state
 * @return {{type: string, diseaseType, assumedSettlement: string, proceduralStatus: (null|*|getters.proceduralStatus|(function(*): (null|*|getters.proceduralStatus|(function(*))|string|computed.proceduralStatus))|string|computed.proceduralStatus), numberOfDependants: (null|*|getters.numberOfDependants|(function(*): (null|*|getters.numberOfDependants|(function(*))|computed.numberOfDependants|{get, set}))|computed.numberOfDependants|{get, set})}}
 */
function claimForServer(state, modal) {
    return {
        type: modal ? (state.claim.type + '_' + state.dependencyType) : state.claim.type,
        diseaseOrAccident: state.claim.diseaseOrAccident,
        diseaseType: state.claim.diseaseType,
        accidentType: state.claim.accidentType,
        assumedSettlement: serverDate(state.claim.assumedSettlement),
        proceduralStatus: state.claim.proceduralStatus,
        numberOfDependants: state.claim.numberOfDependants,
        dependencyType: state.dependencyType
    };
}

/**
 * Make the deceased for the server.
 * @param state
 * @return {{gender: (null|*), dateOfBirth: string, dateOfDeath: string, ageAtDeath: (null|*), employmentType: (null|*), onsetOfSymptoms: string, salary: (null|*), occupation: (deceased.occupation|{code, description}|*)}}
 */
function personForServer(state) {
    return {
        gender: state.person.gender,
        dateOfBirth: serverDate(state.person.dateOfBirth),
        dateOfDeath: serverDate(state.person.dateOfDeath),
        ageAtDeath: state.person.ageAtDeath,
        employmentType: state.person.employmentType,
        onsetOfSymptoms: serverDate(state.person.onsetOfSymptoms),
        salary: state.person.salary,
        occupation: state.person.occupation,
    };
}

// Assign Initial state
const state = initialState();

// Getters
const getters = {
    modalPayload: state => {
        return {
            dependencyType: state.dependencyType,
            claim: claimForServer(state, true),
            person: personForServer(state),
        };
    },
    diseaseOrAccident: state => state.claim.diseaseOrAccident,
    diseaseType: state => state.claim.diseaseType,
    accidentType: state => state.claim.accidentType,
    estateOrDependency: state => state.dependencyType,
    gender: state => state.person.gender,
    dateOfBirth: state => state.person.dateOfBirth,
    dateOfDeath: state => state.person.dateOfDeath,
    ageAtDeath: state => state.person.ageAtDeath,
    assumedSettlement: state => state.claim.assumedSettlement,
    employmentType: state => state.person.employmentType,
    onsetOfSymptoms: state => state.person.onsetOfSymptoms,
    occupation: state => state.person.occupation,
    salary: state => state.person.salary,
    proceduralStatus: state => state.claim.proceduralStatus,
    numberOfDependants: state => state.claim.numberOfDependants,
    ageAtAssessment: state => m(state.claim.assumedSettlement).diff(m(state.person.dateOfBirth), 'years', true),
    claimantName: state => state.nameAndReference.claimantName,
    claimantSolicitors: state => state.nameAndReference.claimantSolicitors,
    claimantReference: state => state.nameAndReference.claimantReference,
    yourReference: state => state.nameAndReference.yourReference,
    defendantName: state => state.nameAndReference.defendantName,
    hasResults: state => state.hasResults,
    isValid: state => {
        let toTest = [
            state.dependencyType,
            state.person.gender,
            state.person.dateOfBirth,
            state.person.dateOfDeath,
            state.person.ageAtDeath,
            state.person.employmentType,
            state.person.onsetOfSymptoms,
            state.claim.diseaseOrAccident === 'disease' ? state.claim.diseaseType : true,
            state.claim.diseaseOrAccident === 'accident' ? state.claim.accidentType : true,
            state.claim.assumedSettlement,
            state.claim.proceduralStatus,
            state.person.employmentType === 'employed' ? state.person.income : true,
            state.dependencyType === 'dependency' ? state.claim.numberOfDependants : true,
        ];
        return toTest.every(notNull);
    },
    results: state => state.results,
    claimantContribution: state => state.contributions.claimant,
    defenceContribution: state => state.contributions.defence,
    damagesContribution: state => state.contributions.damages,
    apportionedClaimantReserve: state => state.results.reserve.claimantCosts * (state.contributions.claimant / 100) || 0,
    apportionedDefenceReserve: state => state.results.reserve.defenceCosts * (state.contributions.defence / 100) || 0,
    apportionedDamagesReserve: (state, getters) => getters.totalDamages * (state.contributions.damages / 100) || 0,
    totalReserve: (state, getters) => getters.apportionedClaimantReserve
        + getters.apportionedDefenceReserve
        + getters.apportionedDamagesReserve,
    totalDamages: state => state.results.damages.totalDamages
        + state.ownHeadsOfLoss.reduce((total, head) => total + head.value, 0),
    chartData: state => state.results.chart,
    durationOfSymptoms: state => m(state.person.dateOfDeath).diff(m(state.person.onsetOfSymptoms), 'months'),
    isValidForSave: (state, getters, rootState) => {
        return (
            (rootState.user.user.hideNames || state.nameAndReference.claimantName !== null) &&
            state.nameAndReference.claimantSolicitors.name !== null &&
            state.nameAndReference.claimantReference !== null &&
            state.nameAndReference.yourReference !== null &&
            state.nameAndReference.defendantName.name !== null &&
            state.nameAndReference.claimantName !== '' &&
            state.nameAndReference.claimantSolicitors.name !== '' &&
            state.nameAndReference.claimantReference !== '' &&
            state.nameAndReference.yourReference !== '' &&
            state.nameAndReference.defendantName.name !== ''
        );
    },
    exists: state => !!state.claimId,
    ownHeadsOfLoss: state => state.ownHeadsOfLoss,
    claimId: state => state.claimId,
    calculating: state => state.calculating,
    noChartData: state => !state.results.chart || !state.results.chart.count,
    claimType: state => state.claim.type,
    defenceCosts: state => state.results.reserve.defenceCosts,
    claimantCosts: state => state.results.reserve.claimantCosts,
    dirtyForm: state => state.dirtyForm,
};

// Actions
const actions = {
    /**
     * Commit a form value change to the state.
     *
     * Single entry point for all other item that must fire when changed.
     *
     * @param {context}
     * @param {{key:string, value: any}}
     * @return {Promise}
     */
    updateValue({commit, dispatch}, {key, value}) {
        switch (key) {
            case 'estateOrDependency':
                commit(mutationTypes.SET_FORM_DIRTY);
                return commit(mutationTypes.UPDATE_DEPENDENCY_TYPE, value);
            case 'dateOfBirth':
            case 'dateOfDeath':
            case 'ageAtDeath':
                commit(mutationTypes.SET_FORM_DIRTY);
                return dispatch('updateAge', {[key]: value})
                    .then(() => commit(mutationTypes.UPDATE_DECEASED, {key, value}));
            case 'gender':
            case 'employmentType':
            case 'onsetOfSymptoms':
            case 'occupation':
            case 'salary':
                commit(mutationTypes.SET_FORM_DIRTY);
                return commit(mutationTypes.UPDATE_DECEASED, {key, value});
            case 'diseaseType':
            case 'accidentType':
            case 'diseaseOrAccident':
            case 'assumedSettlement':
            case 'proceduralStatus':
            case 'numberOfDependants':
                commit(mutationTypes.SET_FORM_DIRTY);
                return commit(mutationTypes.UPDATE_CLAIM, {key, value});
            case 'claimantContribution':
                return commit(mutationTypes.UPDATE_CONTRIBUTION, {type: 'claimant', value});
            case 'defenceContribution':
                return commit(mutationTypes.UPDATE_CONTRIBUTION, {type: 'defence', value});
            case 'damagesContribution':
                return commit(mutationTypes.UPDATE_CONTRIBUTION, {type: 'damages', value});
            case 'claimantName':
            case 'claimantSolicitors':
                return commit(mutationTypes.UPDATE_NAME_AND_REFERENCE, {key, value});
            case 'claimantReference':
            case 'yourReference':
            case 'defendantName':
                return commit(mutationTypes.UPDATE_NAME_AND_REFERENCE, {key, value});
            case 'type':
                return commit(mutationTypes.UPDATE_CLAIM, {key, value});
            case 'defenceCosts':
                return commit(mutationTypes.UPDATE_COSTS, {key, value});
            case 'claimantCosts':
                return commit(mutationTypes.UPDATE_COSTS, {key, value});
            case 'generalDamages':
                return commit(mutationTypes.UPDATE_GENERAL_DAMAGES, value);
        }
    },


    /**
     * Perform calculation
     *
     * Reset has values flag and call API then update the results.
     *
     * @param {context}
     * @return {Promise}
     */
    performCalculation({commit, state}) {
        commit(mutationTypes.RESET_RESULT);
        commit(mutationTypes.START_CALCULATING);
        return Api.dashboards.reserving
          .simpleAssessment(
            claimForServer(state),
            personForServer(state)
          )
          .then(results => Object.keys(state.results)
              .map(key => commit(mutationTypes.UPDATE_RESULT, {
                      type: key,
                      data: camelCaseObjectKeys(results[key])
                  })
              )
          )
          .then(() => commit(mutationTypes.SET_FORM_CLEAN))
          .then(() => commit(mutationTypes.STOP_CALCULATING))
    },

    /**
     * Update the age and keep in sync.
     *
     * Bit convoluted this but it works Â¯\_(ãƒ„)_/Â¯
     *
     * @param {context}
     * @param {{dateOfBirth:string,dateOfDeath:string,ageAtDeath:number}}
     * @return {Promise}
     */
    updateAge({commit, state}, {dateOfBirth, dateOfDeath, ageAtDeath}) {
        switch (true) {
            //Setting two at once
            case !!(dateOfBirth && dateOfDeath):
                return commit(mutationTypes.SET_AGE, {
                    dateOfBirth,
                    dateOfDeath,
                    ageAtDeath: m(dateOfDeath).diff(m(dateOfBirth), 'years', true)
                });
            case !!(dateOfBirth && ageAtDeath):
                return commit(mutationTypes.SET_AGE, {
                    dateOfBirth,
                    dateOfDeath: m(dateOfBirth).add(ageAtDeath, 'years').format('DD/MM/YYYY'),
                    ageAtDeath
                });
            case !!(dateOfDeath && ageAtDeath):
                return commit(mutationTypes.SET_AGE, {
                    dateOfBirth: m(dateOfDeath).subtract(ageAtDeath, 'years').format('DD/MM/YYYY'),
                    dateOfDeath,
                    ageAtDeath
                });
            //Setting one and other is in state
            case !!(dateOfBirth && state.person.dateOfDeath):
                return commit(mutationTypes.SET_AGE, {
                    dateOfBirth,
                    dateOfDeath: state.person.dateOfDeath,
                    ageAtDeath: m(state.person.dateOfDeath).diff(m(dateOfBirth), 'years', true)
                });
            case !!(ageAtDeath && state.person.dateOfBirth):
                return commit(mutationTypes.SET_AGE, {
                    dateOfBirth: state.person.dateOfBirth,
                    dateOfDeath: m(state.person.dateOfBirth).add(ageAtDeath, 'years').format('DD/MM/YYYY'),
                    ageAtDeath
                });
            case !!(dateOfBirth && state.person.ageAtDeath):
                return commit(mutationTypes.SET_AGE, {
                    dateOfBirth,
                    dateOfDeath: m(dateOfBirth).add(state.person.ageAtDeath, 'years').format('DD/MM/YYYY'),
                    ageAtDeath: state.person.ageAtDeath
                });
            case !!(ageAtDeath && state.person.dateOfDeath):
                return commit(mutationTypes.SET_AGE, {
                    dateOfBirth: m(state.person.dateOfDeath).subtract(ageAtDeath, 'years').format('DD/MM/YYYY'),
                    dateOfDeath: state.person.dateOfDeath,
                    ageAtDeath
                });
            case !!(dateOfDeath && state.person.dateOfBirth):
                return commit(mutationTypes.SET_AGE, {
                    dateOfBirth: state.person.dateOfBirth,
                    dateOfDeath,
                    ageAtDeath: m(dateOfDeath).diff(m(state.person.dateOfBirth), 'years', true)
                });
            case !!(dateOfDeath && state.person.ageAtDeath):
                return commit(mutationTypes.SET_AGE, {
                    dateOfBirth: m(state.person.dateOfDeath).subtract(state.person.ageAtDeath, 'years').format('DD/MM/YYYY'),
                    dateOfDeath,
                    ageAtDeath: state.person.ageAtDeath
                });
            // Just set one value
            case !!(dateOfBirth):
                return commit(mutationTypes.UPDATE_DECEASED, {key: 'dateOfBirth', value: dateOfBirth});
            case !!(dateOfDeath):
                return commit(mutationTypes.UPDATE_DECEASED, {key: 'dateOfDeath', value: dateOfDeath});
            case !!(ageAtDeath):
                return commit(mutationTypes.UPDATE_DECEASED, {key: 'ageAtDeath', value: ageAtDeath});
        }
    },

    /**
     * Reset to initial state.
     *
     * @param {context}
     * @return {Promise}
     */
    reset({commit}) {
        return commit(mutationTypes.RESET);
    },

    /**
     * Persist state.
     *
     * @param {context}
     * @param {number} [claimId] optional claim id
     * @param {string} [claimType] optional claim type
     */
    save({commit, state}, claimId = state.claimId) {
        return Api.claims.save({
            claim_id: claimId,
            step_id: state.stepId,
            claim_type: 'day_one_only_fatal_accident',
            step_number: 0,
            data: {
                dependencyType: state.dependencyType,
                nameAndReference: state.nameAndReference,
                claim: claimForServer(state),
                person: personForServer(state),
                results: {
                    calculation: state.results.calculation,
                    damages: state.results.damages,
                    reserve: state.results.reserve,
                    chart: state.results.chart
                },
                contributions: state.contributions,
                ownHeadsOfLoss: state.ownHeadsOfLoss
            }
        }).then(result => {
            commit(mutationTypes.UPDATE_CLAIM_ID, {claimId: result.step.claim_id, stepId: result.step.step_id});
            return result.step.claim_id;
        });
    },

    /**
     * Open claim as a fatal accident claim.
     * @param {context}
     * @return {Promise}
     */
    openAsClaim({dispatch, state}) {
        let updateClaim = (key, value) => dispatch('fatal/' + state.dependencyType + '/claimDetails/update', {
            key,
            value
        }, {root: true});
        let dispatchTo = 'fatal/' + state.dependencyType + '/deceased/updateTheDeceased';

        //Reset claim
        return dispatch('claim/reset', null, {root: true})
        // Initialise claim
            .then(() => dispatch('claim/initialise', state.claim.type + '_' + state.dependencyType, {root: true}))
            // Update the claim id
            .then(() => dispatch('claim/setClaimId', state.claimId, {root: true}))
            // Update the menu
            .then(() => dispatch('menu/updateSubMenu', 'fatal/' + state.dependencyType, {root: true}))
            // Open the menu
            .then(() => dispatch('menu/updateMenu', '', {root: true}))
            // Set details from day one
            .then(() => Promise.all([
                    // Claim Details
                    updateClaim('claimantName', state.nameAndReference.claimantName),
                    updateClaim('defendantName', state.nameAndReference.defendantName),
                    updateClaim('claimantSolicitor', state.nameAndReference.claimantSolicitors),
                    updateClaim('claimantReference', state.nameAndReference.claimantReference),
                    updateClaim('yourReference', state.nameAndReference.yourReference),
                    updateClaim('assumedSettlement', state.claim.assumedSettlement),
                    updateClaim('diseaseOrAccident', state.claim.diseaseOrAccident),
                    updateClaim('diseaseType', state.claim.diseaseType),
                    updateClaim('accidentType', state.claim.accidentType),
                    // Deceased Details
                    dispatch(dispatchTo, {
                        gender: state.person.gender,
                        dateOfBirth: state.person.dateOfBirth,
                        dateOfDeath: state.person.dateOfDeath,
                        onsetOfSymptoms: state.person.onsetOfSymptoms
                    }, {root: true})
                ])
            )
            .then(() => state.claimId);
    },

    load({commit}, response) {
        const step = response.steps[0];

        commit(mutationTypes.UPDATE_FROM_SERVER_STEP, step);

        if (response.claimant_solicitor_id) {
            commit(mutationTypes.UPDATE_CLAIMANT_SOLICITOR_ID, response.claimant_solicitor_id);
        }

        if (response.defendant_id) {
            commit(mutationTypes.UPDATE_DEFENDANT_ID, response.defendant_id);
        }

        return;
    },
    addHeadOfLoss({commit}) {
        return commit(mutationTypes.ADD_OWN_HEAD_OF_LOSS);
    },
    updateHeadOfLoss({commit}, {index, key, value}) {
        return commit(mutationTypes.UPDATE_OWN_HEAD_OF_LOSS, {index, key, value});
    },
    deleteHeadOfLoss({commit}, index) {
        return commit(mutationTypes.DELETE_OWN_HEAD_OF_LOSS, index);
    }
};

const mutations = {
    [mutationTypes.UPDATE_DECEASED](state, {key, value}) {
        state.person[key] = value;
    },
    [mutationTypes.UPDATE_DEPENDENCY_TYPE](state, value) {
        state.dependencyType = value;
    },
    [mutationTypes.UPDATE_CLAIM](state, {key, value}) {
        state.claim[key] = value;
    },
    [mutationTypes.UPDATE_NAME_AND_REFERENCE](state, {key, value}) {
        state.nameAndReference[key] = value;
    },
    [mutationTypes.UPDATE_CLAIMANT_SOLICITOR_ID](state, value) {
        state.nameAndReference.claimantSolicitors.id = value;
    },
    [mutationTypes.UPDATE_DEFENDANT_ID](state, value) {
        state.nameAndReference.defendantName.id = value;
    },
    [mutationTypes.SET_AGE](state, {dateOfBirth, dateOfDeath, ageAtDeath}) {
        state.person.dateOfBirth = dateOfBirth;
        state.person.dateOfDeath = dateOfDeath;
        state.person.ageAtDeath = roundValue.methods.roundValue(ageAtDeath, 2);
        state.person.employmentType = ageAtDeath > 64 ? 'retired' : 'employed';
    },
    [mutationTypes.UPDATE_RESULT](state, {type, data}) {
        state.results[type] = data;
        state.hasResults = true;
    },
    [mutationTypes.RESET_RESULT](state) {
        state.hasResults = false;
    },
    [mutationTypes.UPDATE_CONTRIBUTION](state, {type, value}) {
        state.contributions[type] = value;
    },
    [mutationTypes.RESET](state) {
        Object.assign(state, initialState());
    },
    [mutationTypes.UPDATE_CLAIM_ID](state, {claimId, stepId}) {
        state.claimId = claimId;
        state.stepId = stepId;
    },
    [mutationTypes.UPDATE_FROM_SERVER_STEP](state, step) {
        const data = camelCaseObjectKeysDeep(step.data);
        state.claimId = step.claim_id;
        state.stepId = step.id;
        state.dependencyType = step.data.dependency_type;
        Object.assign(state.claim, checkForServerDates(data.claim));
        Object.assign(state.nameAndReference, checkForServerDates(data.nameAndReference));
        Object.assign(state.person, checkForServerDates(data.person));
        Object.assign(state.results, data.results);
        Object.assign(state.contributions, data.contributions);
        Object.assign(state.ownHeadsOfLoss, data.ownHeadsOfLoss);
        state.hasResults = true;
    },
    [mutationTypes.ADD_OWN_HEAD_OF_LOSS](state) {
        state.ownHeadsOfLoss.push({
            name: null, value: 0
        });
    },
    [mutationTypes.UPDATE_OWN_HEAD_OF_LOSS](state, {index, key, value}) {
        state.ownHeadsOfLoss[index][key] = value;
    },
    [mutationTypes.DELETE_OWN_HEAD_OF_LOSS](state, index) {
        state.ownHeadsOfLoss.splice(index, 1);
    },
    [mutationTypes.START_CALCULATING](state) {
        state.calculating = true;
    },
    [mutationTypes.STOP_CALCULATING](state) {
        state.calculating = false;
    },
    [mutationTypes.SET_FORM_DIRTY](state) {
        state.dirtyForm = true;
    },
    [mutationTypes.SET_FORM_CLEAN](state) {
        state.dirtyForm = false;
    },
    [mutationTypes.UPDATE_COSTS](state, {key, value}) {
        state.results.reserve[key] = value;
    },
    [mutationTypes.UPDATE_GENERAL_DAMAGES](state, value) {
        state.results.damages.totalDamages -= state.results.damages.generalDamages;
        state.results.damages.generalDamages = value;
        state.results.damages.totalDamages += value;
    },


};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};



// WEBPACK FOOTER //
// ./src/store/modules/reserves/dayOneFatal.js